/**
 * Extracts the tenant slug from a hostname: the first dot-separated label.
 *
 * Examples:
 * - `acme.qa.olivelabs.ai` → `acme`
 * - `acme.local.olivelabs.ai` → `acme`
 *
 * Returns `null` for bare `localhost` or IP addresses (no subdomain = no tenant).
 */
export function getTenantFromHostname(hostname: string): string | null {
  const trimmed = hostname.trim().toLowerCase();
  if (!trimmed || isIpAddress(trimmed)) {
    return null;
  }

  const labels = trimmed.split(".");
  if (labels.length < 2) {
    return null;
  }

  return labels[0] || null;
}

/**
 * Returns true when the first hostname label is `admin` (case-insensitive).
 *
 * Examples:
 * - `admin.qa.olivelabs.ai` → `true`
 * - `acme.qa.olivelabs.ai` → `false`
 *
 * Returns `false` for bare `localhost`, IP addresses, or single-label hostnames.
 */
export function isAdminHostname(hostname: string): boolean {
  const trimmed = hostname.trim().toLowerCase();
  if (!trimmed || isIpAddress(trimmed)) {
    return false;
  }

  const labels = trimmed.split(".");
  if (labels.length < 2) {
    return false;
  }

  return labels[0] === "admin";
}

function isIpAddress(hostname: string): boolean {
  if (hostname.includes(":")) {
    return true;
  }
  return /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);
}
